import { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowRight, ArrowDown, Bell, Check, Loader2, Sparkles, Dumbbell, Film, Heart, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { PRICING_2027, PREMIERE_DEADLINE_LABEL, INTERESSI_2027 } from '@/data/pricing2027';

const INTERESSE_QUERY_MAP = {
  abbonamenti: 'Abbonamenti piscina',
  palestra: "Palestra all'aperto",
  jacuzzi: 'Jacuzzi sotto le stelle',
  cinema: 'Cinema estivo',
};

export default function Estate2027() {
  const reduceMotion = useReducedMotion();
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);
  const premiereRef = useRef(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState([]);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const pre = INTERESSE_QUERY_MAP[searchParams.get('interesse')];
    if (pre) {
      setInterests([pre]);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' }), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });

  const preselectAndScroll = (interesse) => {
    setInterests((prev) => (prev.includes(interesse) ? prev : [...prev, interesse]));
    scrollTo(formRef);
  };

  const toggleInterest = (interesse) => {
    setInterests((prev) =>
      prev.includes(interesse) ? prev.filter((i) => i !== interesse) : [...prev, interesse]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Inserisci il tuo nome'); return; }
    if (!email.trim()) { toast.error('Inserisci la tua email'); return; }
    if (interests.length === 0) { toast.error('Seleziona almeno un interesse'); return; }
    if (!gdprConsent) { toast.error("Accetta l'informativa privacy per continuare"); return; }
    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/estate2027/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), interests, gdpr_consent: gdprConsent }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSubmitted(true);
        toast.success(data.already_registered
          ? 'Eri già registrato: abbiamo aggiornato i tuoi interessi.'
          : 'Registrazione completata! Ti terremo aggiornato.');
      } else {
        toast.error(data.detail || "Errore durante l'invio. Riprova.");
      }
    } catch (err) {
      toast.error('Errore di connessione. Riprova.');
    }
    setSubmitting(false);
  };

  const fadeUp = reduceMotion
    ? { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } }
    : { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  const { feriale, fullClub } = PRICING_2027.stagionali;

  return (
    <>
      <Helmet>
        <title>La Villa — Estate 2027 | Piscina, Première e nuove esperienze</title>
        <meta name="description" content="L'anteprima dell'estate 2027 alla Villa: abbonamenti piscina Première, palestra all'aperto, Jacuzzi sotto le stelle e cinema estivo. Lascia il tuo contatto per ricevere le novità." />
        <meta property="og:title" content="La Villa — Estate 2027 | Piscina, Première e nuove esperienze" />
        <meta property="og:description" content="La tua prossima estate comincia qui. Entra nell'anteprima dell'estate 2027 alla Villa." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div data-testid="estate-2027-page">
        {/* ─── HERO ─── */}
        <section className="relative min-h-[70vh] md:min-h-[75vh] flex items-start overflow-hidden" data-testid="estate2027-hero">
          <div className="absolute inset-0">
            <img
              src="/images/estate2027-hero.jpg"
              alt="Un pomeriggio a bordo piscina alla Villa"
              className="w-full h-full object-cover object-[center_30%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-night via-night/60 to-night/30" />
          </div>
          <div className="relative w-full max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 pt-28 md:pt-32 pb-16">
            <motion.p
              className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              La Villa · Summer 2027
            </motion.p>
            <motion.h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight mb-8 max-w-3xl"
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              data-testid="estate2027-title"
            >
              La tua prossima estate. In anteprima.
            </motion.h1>
            <motion.div
              className="font-body text-stone-300 max-w-2xl space-y-4 mb-10 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <p>Il sole sulla pelle, un pomeriggio in piscina, un aperitivo che diventa una serata da ricordare.</p>
              <p>
                Alla Villa stiamo preparando l'estate 2027. Accanto ai momenti che conosci già, prenderanno spazio
                nuove esperienze: una palestra all'aperto, la Jacuzzi da riservare in coppia, il cinema estivo e
                altre novità che sveleremo nei prossimi mesi.
              </p>
              <p className="text-ivory">L'attesa comincia qui.</p>
            </motion.div>
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
            >
              <Button
                onClick={() => scrollTo(premiereRef)}
                className="bg-champagne text-night hover:bg-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="hero-cta-premiere"
              >
                Scopri Première <ArrowDown className="ml-2" size={16} />
              </Button>
              <Button
                onClick={() => scrollTo(formRef)}
                variant="outline"
                className="border-ivory/60 bg-transparent text-ivory hover:bg-ivory hover:text-night rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="hero-cta-anteprime"
              >
                Ricevi le anteprime
              </Button>
            </motion.div>
          </div>
        </section>

        {/* ─── PISCINA E ABBONAMENTI — PREMIÈRE ─── */}
        <section ref={premiereRef} id="premiere" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-ivory" data-testid="estate2027-premiere">
          <div className="max-w-6xl mx-auto">
            <motion.div className="max-w-2xl mb-16" {...fadeUp}>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">La Villa Première</p>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-6">
                La tua estate in piscina, a partire da oggi.
              </h2>
              <div className="font-body text-stone-600 space-y-3 leading-relaxed">
                <p>Un lettino, il verde intorno, un tuffo e la libertà di trascorrere la giornata con il tuo ritmo.</p>
                <p>Con La Villa Première puoi scegliere in anticipo il tuo abbonamento piscina 2027, a un prezzo dedicato.</p>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              {/* Feriale */}
              <motion.div
                className="card-premium bg-white p-8 md:p-10 flex flex-col"
                {...fadeUp}
                transition={{ delay: 0.1 }}
                data-testid="premiere-card-feriale"
              >
                <p className="font-body text-xs uppercase tracking-[0.25em] text-champagne mb-2">{feriale.nome}</p>
                <p className="font-body text-sm text-stone-500 mb-8">{feriale.giorni}</p>
                <div className="mb-2">
                  <p className="font-body text-xs uppercase tracking-widest text-stone-400 mb-1">Prezzo Première</p>
                  <p className="font-heading text-5xl text-night">{feriale.premiere} €</p>
                </div>
                <p className="font-body text-sm text-stone-500 mb-6">
                  Prezzo ordinario, dalla fase successiva: <span className="text-night font-medium">{feriale.ordinario} €</span>
                </p>
                <div className="section-divider mb-6" />
                <p className="font-body text-sm text-stone-600 flex items-start gap-2 mb-8">
                  <Check size={16} className="text-champagne mt-0.5 shrink-0" /> {feriale.lettino}
                </p>
                <Button
                  onClick={() => preselectAndScroll('Abbonamenti piscina')}
                  variant="outline"
                  className="mt-auto border-night text-night hover:bg-night hover:text-ivory rounded-full py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                  data-testid="premiere-feriale-notify-btn"
                >
                  <Bell size={16} className="mr-2" /> Avvisami all'apertura delle vendite
                </Button>
              </motion.div>

              {/* Full Club — maggiore evidenza */}
              <motion.div
                className="card-premium bg-night text-ivory p-8 md:p-10 flex flex-col border border-champagne/40 relative"
                {...fadeUp}
                transition={{ delay: 0.2 }}
                data-testid="premiere-card-fullclub"
              >
                <p className="font-body text-xs uppercase tracking-[0.25em] text-champagne mb-2">{fullClub.nome}</p>
                <p className="font-body text-sm text-stone-400 mb-8">{fullClub.giorni}</p>
                <div className="mb-2">
                  <p className="font-body text-xs uppercase tracking-widest text-stone-500 mb-1">Prezzo Première</p>
                  <p className="font-heading text-5xl text-champagne">{fullClub.premiere} €</p>
                </div>
                <p className="font-body text-sm text-stone-400 mb-6">
                  Prezzo ordinario, dalla fase successiva: <span className="text-ivory font-medium">{fullClub.ordinario} €</span>
                </p>
                <div className="w-12 h-px bg-champagne/40 mb-6" />
                <p className="font-body text-sm text-stone-300 flex items-start gap-2 mb-8">
                  <Check size={16} className="text-champagne mt-0.5 shrink-0" /> {fullClub.lettino}
                </p>
                <Button
                  onClick={() => preselectAndScroll('Abbonamenti piscina')}
                  className="mt-auto bg-champagne text-night hover:bg-ivory rounded-full py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                  data-testid="premiere-fullclub-notify-btn"
                >
                  <Bell size={16} className="mr-2" /> Avvisami all'apertura delle vendite
                </Button>
              </motion.div>
            </div>

            <motion.div className="mt-10 text-center space-y-4" {...fadeUp}>
              <p className="font-body text-sm text-stone-600">
                La finestra Première termina il <strong className="text-night">{PREMIERE_DEADLINE_LABEL}</strong>.
              </p>
              <Link
                to="/abbonamenti"
                className="inline-flex items-center gap-1 font-body text-sm text-stone-500 underline underline-offset-4 hover:text-night transition-colors"
                data-testid="link-tutte-le-formule"
              >
                Scopri tutte le formule <ArrowRight size={14} />
              </Link>
              <p className="font-body text-xs text-stone-400 max-w-2xl mx-auto leading-relaxed">
                Gli abbonamenti comprendono l'accesso piscina e i servizi indicati nella formula scelta.
                Palestra, esperienze Jacuzzi, acquagym ed eventi serali prevedono condizioni e tariffe dedicate.
              </p>
              <p className="font-body text-xs text-stone-400">
                Date di apertura e chiusura, orari e condizioni della stagione 2027 in definizione.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ─── JACUZZI SOTTO LE STELLE ─── */}
        <section className="relative py-32 md:py-44 px-6 md:px-12 lg:px-24 overflow-hidden" data-testid="estate2027-jacuzzi">
          <div className="absolute inset-0">
            <img
              src="/images/jacuzzi-2027.jpg"
              alt="Jacuzzi sotto le stelle — atmosfera serale"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-night/45" />
            <div className="absolute inset-0 bg-gradient-to-l from-night/95 via-night/75 to-night/40" />
          </div>
          <div className="relative max-w-6xl mx-auto flex justify-end">
            <motion.div className="max-w-xl text-left" {...fadeUp}>
              <div className="flex items-center gap-3 mb-4">
                <Heart size={18} className="text-champagne" />
                <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne">Un'esperienza da vivere in due</p>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-3">
                Jacuzzi sotto le stelle.
              </h2>
              <p className="font-heading text-xl text-champagne/90 mb-6">Una sera da riservare a voi due.</p>
              <div className="font-body text-stone-200 space-y-3 leading-relaxed mb-6">
                <p>
                  Stiamo preparando un'esperienza da vivere in coppia: Jacuzzi riservata in esclusiva, aperitivo e
                  l'atmosfera della Villa dopo il tramonto.
                </p>
                <p>
                  Per un anniversario, una sorpresa o una serata da concedervi senza aspettare un'occasione speciale.
                </p>
              </div>
              <p className="font-body text-xs text-stone-300 mb-8">
                Esperienza prenotabile separatamente. Formule, prezzi e disponibilità saranno svelati prossimamente.
              </p>
              <Button
                onClick={() => preselectAndScroll('Jacuzzi sotto le stelle')}
                className="bg-champagne text-night hover:bg-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                data-testid="jacuzzi-notify-btn"
              >
                <Bell size={16} className="mr-2" /> Avvisami quando sarà prenotabile
              </Button>
            </motion.div>
          </div>
        </section>

        {/* ─── PALESTRA ALL'APERTO ─── */}
        <section className="relative py-32 md:py-44 px-6 md:px-12 lg:px-24 overflow-hidden" data-testid="estate2027-palestra">
          <div className="absolute inset-0">
            <img
              src="/images/palestra-2027.jpg"
              alt="Atmosfera della palestra all'aperto in preparazione"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-night/50" />
            <div className="absolute inset-0 bg-gradient-to-r from-night/95 via-night/80 to-night/45" />
          </div>
          <div className="relative max-w-6xl mx-auto">
            <motion.div className="max-w-xl" {...fadeUp}>
              <div className="flex items-center gap-3 mb-4">
                <Dumbbell size={20} className="text-champagne" />
                <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne">Novità 2027</p>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-6">
                Allenati all'aria aperta.
              </h2>
              <div className="font-body text-stone-200 space-y-3 leading-relaxed max-w-2xl mb-8">
                <p>Il verde intorno. Il tuo momento per ricaricarti.</p>
                <p>
                  Tra le novità in preparazione per l'estate 2027 ci sarà una palestra all'aperto: uno spazio dedicato
                  al movimento, per aggiungere una nuova abitudine alle tue giornate alla Villa.
                </p>
                <p>Presto presenteremo l'area, le attrezzature e le formule di accesso.</p>
              </div>
              <div className="border-l-2 border-champagne pl-5 py-1 mb-8 bg-night/40 rounded-r">
                <p className="font-body text-sm text-champagne">
                  Accesso con tariffa dedicata, separata dall'ingresso e dagli abbonamenti piscina.
                </p>
              </div>
              <p className="font-heading text-xl text-ivory tracking-wide">Stay tuned.</p>
            </motion.div>
          </div>
        </section>

        {/* ─── CINEMA ESTIVO ─── */}
        <section className="relative py-32 md:py-44 px-6 md:px-12 lg:px-24 overflow-hidden" data-testid="estate2027-cinema">
          <div className="absolute inset-0">
            <img
              src="/images/cinema-2027.jpg"
              alt="Cinema all'aperto a bordo piscina — atmosfera serale"
              className="w-full h-full object-cover object-[center_25%]"
            />
            <div className="absolute inset-0 bg-night/50" />
            <div className="absolute inset-0 bg-gradient-to-t from-night/95 via-night/60 to-night/50" />
          </div>
          <div className="relative max-w-4xl mx-auto text-center">
            <motion.div {...fadeUp}>
              <div className="flex items-center justify-center gap-3 mb-4">
                <Film size={20} className="text-champagne" />
                <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne">Novità 2027</p>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-6">
                Ci vediamo sotto il grande schermo.
              </h2>
              <p className="font-body text-stone-200 leading-relaxed max-w-2xl mx-auto mb-6">
                Nelle sere d'estate, la Villa si preparerà ad accogliere anche il cinema all'aperto: un nuovo
                appuntamento da condividere con gli amici o in coppia, immersi nell'atmosfera del parco.
                La programmazione, le date e le modalità di accesso arriveranno nei prossimi mesi.
              </p>
              <p className="font-body text-sm text-stone-300 italic">Il programma è in preparazione.</p>
            </motion.div>
          </div>
        </section>

        {/* ─── LE NOVITÀ CONTINUANO — FORM ─── */}
        <section ref={formRef} id="anteprime" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-night" data-testid="estate2027-form-section">
          <div className="max-w-2xl mx-auto">
            <motion.div className="text-center mb-12" {...fadeUp}>
              <Sparkles size={22} className="text-champagne mx-auto mb-4" />
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-6">
                Le novità continuano.
              </h2>
              <div className="font-body text-stone-400 space-y-3 leading-relaxed">
                <p>Questa è soltanto la prima anticipazione dell'estate 2027.</p>
                <p>
                  Ti accompagneremo nei preparativi, mostrandoti i nuovi spazi e svelando le esperienze in arrivo,
                  un aggiornamento alla volta.
                </p>
                <p>
                  Lascia il tuo contatto per ricevere le novità su Première, la palestra, l'apertura delle
                  prenotazioni della Jacuzzi e le prime serate di cinema.
                </p>
              </div>
            </motion.div>

            {submitted ? (
              <motion.div
                className="text-center border border-champagne/40 bg-white/5 rounded-2xl p-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="estate2027-form-success"
              >
                <Check size={32} className="text-champagne mx-auto mb-4" />
                <h3 className="font-heading text-2xl text-ivory mb-3">Sei dentro l'anteprima.</h3>
                <p className="font-body text-stone-400 text-sm">
                  Ti scriveremo appena ci saranno novità sulle esperienze che hai scelto.
                </p>
              </motion.div>
            ) : (
              <motion.form onSubmit={handleSubmit} className="space-y-6" {...fadeUp} data-testid="estate2027-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="e27-name" className="text-stone-300 text-sm">Nome</Label>
                    <Input
                      id="e27-name"
                      type="text"
                      placeholder="Il tuo nome"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-2 bg-white/5 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne rounded-full px-6 py-6"
                      data-testid="estate2027-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="e27-email" className="text-stone-300 text-sm">Email</Label>
                    <Input
                      id="e27-email"
                      type="email"
                      placeholder="email@esempio.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-2 bg-white/5 border-stone-700 text-ivory placeholder:text-stone-500 focus:border-champagne rounded-full px-6 py-6"
                      data-testid="estate2027-email-input"
                    />
                  </div>
                </div>

                <div>
                  <p className="font-body text-sm text-stone-300 mb-3">Cosa ti interessa?</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {INTERESSI_2027.map((interesse) => (
                      <button
                        key={interesse}
                        type="button"
                        onClick={() => toggleInterest(interesse)}
                        className={`flex items-center gap-3 rounded-full border px-5 py-3.5 text-left font-body text-sm transition-colors ${
                          interests.includes(interesse)
                            ? 'border-champagne bg-champagne/15 text-champagne'
                            : 'border-stone-700 text-stone-400 hover:border-stone-500'
                        }`}
                        data-testid={`interesse-${interesse.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        aria-pressed={interests.includes(interesse)}
                      >
                        <span className={`flex h-4 w-4 items-center justify-center rounded-full border shrink-0 ${
                          interests.includes(interesse) ? 'border-champagne bg-champagne' : 'border-stone-600'
                        }`}>
                          {interests.includes(interesse) && <Check size={11} className="text-night" />}
                        </span>
                        {interesse}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="e27-gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-0.5 border-stone-600 data-[state=checked]:bg-champagne data-[state=checked]:border-champagne"
                    data-testid="estate2027-gdpr-checkbox"
                  />
                  <Label htmlFor="e27-gdpr" className="font-body text-xs text-stone-500 leading-relaxed cursor-pointer">
                    Accetto la <Link to="/privacy" className="underline hover:text-ivory">Privacy Policy</Link> e acconsento
                    al trattamento dei miei dati per ricevere comunicazioni da La Villa sulle novità dell'estate 2027.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-champagne text-night hover:bg-ivory rounded-full py-6 text-sm uppercase tracking-widest"
                  data-testid="estate2027-submit-btn"
                >
                  {submitting ? <Loader2 className="animate-spin" size={20} /> : 'Voglio ricevere le anteprime'}
                </Button>
              </motion.form>
            )}
          </div>
        </section>

        {/* ─── EVENTI PRIVATI ─── */}
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-ivory" data-testid="estate2027-eventi-privati">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <motion.div className="order-2 md:order-1" {...fadeUp}>
              <div className="flex items-center gap-3 mb-4">
                <PartyPopper size={18} className="text-champagne" />
                <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne">Eventi Privati</p>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-6">
                Nel frattempo, La Villa è tua.
              </h2>
              <p className="font-body text-stone-600 leading-relaxed mb-8">
                Compleanni, lauree, cene aziendali e occasioni speciali: durante l'inverno continuiamo ad accogliere
                i tuoi eventi privati. Scopri le sale e le possibilità di riservare gli spazi della Villa per la tua festa.
              </p>
              <Button
                asChild
                className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="estate2027-eventi-cta"
              >
                <Link to="/eventi-privati">
                  Organizza il tuo evento <ArrowRight className="ml-2" size={16} />
                </Link>
              </Button>
            </motion.div>
            <motion.div className="order-1 md:order-2" {...fadeUp} transition={{ delay: 0.15 }}>
              <img
                src="/images/il-tuo-evento.jpg"
                alt="Eventi privati alla Villa"
                className="w-full h-[320px] md:h-[420px] object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
